import {Alert, Button, Checkbox, Group, Modal, Select, Stack, Text} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {addDays} from 'date-fns'
import {useRef, useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {useEditRights} from 'src/jobs/edit-rights-context'
import {formatJobDate, toApiDateString, toLocalDate} from 'src/jobs/job-schedule-format'
import {useProject} from 'src/projects/use-projects'

import {emailedSentence} from './emailed-sentence'
import {openPickups} from './pickup-selection'
import {usePickupCandidates, usePickupSooner} from './use-work'

type PickupSoonerButtonProps = {
    projectId: string
    fromJobId: string | null
    pickupJobId?: string | null
    onDone?: () => void
}

type PickupChoice = {id: string; displayId: string; startDate: string; dateTbc?: boolean}

type PickupSoonerFormValues = {
    dateTbc: boolean
    date: Date
    chosenId: string | null
}

function buildPickupSoonerInitialValues(): PickupSoonerFormValues {
    return {dateTbc: false, date: addDays(businessCalendarDate(), 1), chosenId: null}
}

function formatPickupWhen(pickup: PickupChoice) {
    return pickup.dateTbc ? 'date TBC' : formatJobDate(pickup.startDate)
}

export function PickupSoonerButton(props: PickupSoonerButtonProps) {
    const {canDispatch} = useEditRights()
    return canDispatch ? <PickupSoonerDialogButton {...props} /> : null
}

function PickupSoonerDialogButton({
    projectId,
    fromJobId,
    pickupJobId = null,
    onDone,
}: PickupSoonerButtonProps) {
    const {data: unitsOut = []} = usePickupCandidates(projectId)
    const {data: order} = useProject(projectId)
    const sooner = usePickupSooner(projectId)
    const pickups: PickupChoice[] = openPickups(order?.jobs ?? []).filter(
        (pickup) => pickupJobId === null || pickup.id === pickupJobId,
    )
    const [opened, setOpened] = useState(false)
    const form = useForm<PickupSoonerFormValues>({initialValues: buildPickupSoonerInitialValues()})
    const {dateTbc, date, chosenId} = form.values
    const [error, setError] = useState<string | null>(null)
    // Blocks a second submit before the loading state renders.
    const savingRef = useRef(false)

    if (pickupJobId === null && pickups.length === 0 && unitsOut.length === 0) {
        return null
    }

    const chosen =
        pickups.length === 1 ? pickups[0] : pickups.find((pickup) => pickup.id === chosenId)
    const needsChoice = pickups.length > 1 && !chosen

    function open() {
        form.setValues(buildPickupSoonerInitialValues())
        setError(null)
        setOpened(true)
    }

    async function handleSave() {
        if (savingRef.current) {
            return
        }

        savingRef.current = true
        setError(null)
        try {
            const result = await sooner.mutateAsync({
                startDate: dateTbc ? null : toApiDateString(date),
                pickupJobId: chosen?.id ?? pickupJobId,
                fromJobId,
            })
            setOpened(false)
            if (result.unchanged) {
                onDone?.()
                return
            }
            notifications.show({
                color: 'green',
                title: result.created ? 'Pickup scheduled' : 'Pickup moved',
                message: `${dateTbc ? 'Date TBC' : formatJobDate(date)}. ${emailedSentence(result.dispatchEmailed)}`,
            })
            if (result.unitsNotOnPickup > 0) {
                notifications.show({
                    color: 'orange',
                    title: 'Units not on pickup',
                    message: `${result.unitsNotOnPickup} more still out. Use + Pickup to add them.`,
                })
            }
            onDone?.()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not move the pickup')
        } finally {
            savingRef.current = false
        }
    }

    return (
        <>
            <Button variant="default" onClick={open}>
                Pickup sooner
            </Button>
            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Pickup sooner (released early)"
            >
                <Stack gap="md">
                    {pickupJobId !== null && pickups.length === 0 ? (
                        <Text size="sm">Moves this pickup and emails dispatch.</Text>
                    ) : pickups.length === 0 ? (
                        <Text size="sm">
                            Schedules a pickup for all {unitsOut.length}{' '}
                            {unitsOut.length === 1 ? 'unit' : 'units'} still out and emails
                            dispatch.
                        </Text>
                    ) : pickups.length > 1 ? (
                        <Select
                            label="Which pickup?"
                            placeholder="Select pickup"
                            data={pickups.map((pickup) => ({
                                value: pickup.id,
                                label: `${pickup.displayId} (${formatPickupWhen(pickup)})`,
                            }))}
                            {...form.getInputProps('chosenId')}
                        />
                    ) : chosen ? (
                        <Text size="sm" data-testid="pickup-sooner-current">
                            Moves pickup {chosen.displayId} (now {formatPickupWhen(chosen)}) and
                            emails dispatch.
                        </Text>
                    ) : null}
                    {error ? <Alert color="red">{error}</Alert> : null}
                    <Checkbox
                        label="Date TBC"
                        {...form.getInputProps('dateTbc', {type: 'checkbox'})}
                    />
                    {dateTbc ? null : (
                        <DateInput
                            label="New pickup date"
                            value={date}
                            minDate={businessCalendarDate()}
                            onChange={(value) =>
                                value && form.setFieldValue('date', toLocalDate(value))
                            }
                        />
                    )}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setOpened(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void handleSave()}
                            loading={sooner.isPending}
                            disabled={needsChoice}
                        >
                            {pickupJobId === null && pickups.length === 0
                                ? 'Schedule pickup'
                                : 'Move pickup'}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    )
}
